import { AnyAction } from '@reduxjs/toolkit';
import _uniq from 'lodash/uniq';
import type { Job } from 'types';
import { JobBucket } from 'types/reducers/jobs';

import { filterBadValues } from 'utils';

import { jobInitialState, JOBS } from '../initialStates/jobs';

export const ACTION_TYPES = {
  JOBS_LOADING: 'JOBS_LOADING',
  ERROR_FETCH: 'ERROR_FETCH',
  FETCHED: 'JOBS_FETCHED',
  FETCH_MORE: 'FETCH_MORE',
  PAID_JOBS_LOADING: 'PAID_JOBS_LOADING',
  JOB_ADD_SAVE: 'JOB_ADD_SAVE',
  JOB_REMOVE_SAVE: 'JOB_REMOVE_SAVE',
  JOB_ADD_DISMISS: 'JOB_ADD_DISMISS',
  JOB_REMOVE_DISMISS: 'JOB_REMOVE_DISMISS',
  LISTING_HASH: 'LISTING_HASH',
  JOB_ADD_SEEN: 'JOB_ADD_SEEN',
  JOB_ADD_APPLIED: 'JOB_ADD_APPLIED',

  JOB_CREATE_BUCKET: 'JOB_CREATE_BUCKET',
  JOB_DISPOSE_BUCKET: 'JOB_DISPOSE_BUCKET',
  CURRENT_PAGE_CATEGORY: 'CURRENT_PAGE_CATEGORY',
  JOB_LIST_DISMISS: 'JOB_LIST_DISMISS',

  ALERTS_RECENT_PAYLOAD_SET: 'ALERTS_RECENT_PAYLOAD_SET',
  ALERTS_IS_LOADING_SET: 'ALERTS_IS_LOADING_SET',
  ALERTS_IS_SUBSCRIBED_SET: 'ALERTS_IS_SUBSCRIBED_SET',

  RESET_FETCHED_PAID_HASHES: 'RESET_FETCHED_PAID_HASHES',
};

const jobsReducer = (state = jobInitialState, action: AnyAction) => {
  const { payload } = action;
  let prev: JobBucket = {} as JobBucket;
  if (payload) {
    prev = state[payload.type] || {
      loading: false,
      error: null,
      jobs: [],
      totalJobs: 0,
    };
  }
  let dismissedJobs = [...state.dismissedJobs];
  switch (action.type) {
    case ACTION_TYPES.PAID_JOBS_LOADING:
    case ACTION_TYPES.JOBS_LOADING:
      if (prev) {
        return {
          ...state,
          [payload.type]: {
            ...prev,
            dismissedListingHashes: payload.dismissedListingHashes || [],
            // set filter detail on jobs_loading
            filterDetail: {
              ...(payload.filterDetail || {}),
              previousListingHashes: filterBadValues(
                payload.filterDetail.previousListingHashes || []
              ),
            },
            jobs: payload.loadMore ? prev.jobs : [],
            totalJobs: payload.loadMore ? prev.totalJobs : 0,
            loading: true,
            error: null,
          },
        };
      }
      console.warn(`there is no ${payload.type} which you mentioned`);
      return state;
    case ACTION_TYPES.ERROR_FETCH:
      return {
        ...state,
        [payload.type]: {
          ...prev,
          loading: false,
          error: payload.error ? payload.error : 'error while getting jobs',
          hideOnEmpty: payload.hideOnEmpty,
        },
      };
    case ACTION_TYPES.FETCHED:
      if (prev) {
        return {
          ...state,
          ...(payload.isPaid
            ? {
                receivedPaidListingHashes: [
                  ...state.receivedPaidListingHashes,
                  ...payload.result.jobs
                    .map((job) =>
                      payload.keyToUseAsPrevious && job[payload.keyToUseAsPrevious]
                        ? job[payload.keyToUseAsPrevious]
                        : job.listingHash
                    )
                    .filter(Boolean),
                ],
              }
            : {}),
          [payload.type]: {
            ...prev,
            loading: false,
            error: null,
            remainingJobs:
              payload.result.remainingJobs ||
              (payload.result.totalJobs || 0) - (payload.result.jobs?.length || 0),
            pageNo: 0,
            jobs: payload.result.jobs,
            totalJobs: payload.result.totalJobs,
            hideOnEmpty: payload.result.hideOnEmpty,
            ...(payload.result?.apiParams ? { apiParams: payload.result?.apiParams } : {}),
            ...(payload.result?.searchJobTitles
              ? { searchJobTitles: payload.result?.searchJobTitles }
              : {}),
            // reset previousListingHashes with newly loaded jobs
            filterDetail: {
              ...prev.filterDetail,
              previousListingHashes: filterBadValues([
                ...(prev.filterDetail?.previousListingHashes || []),
                ...payload.result.jobs
                  .map((job) =>
                    payload.keyToUseAsPrevious && job[payload.keyToUseAsPrevious]
                      ? job[payload.keyToUseAsPrevious]
                      : job.listingHash
                  )
                  .filter(Boolean),
              ]),
            },
          },
        };
      }
      console.warn(`there is no ${payload.type} which you mentioned`);
      return state;
    case ACTION_TYPES.FETCH_MORE:
      if (prev) {
        return {
          ...state,
          ...(payload.isPaid
            ? {
                receivedPaidListingHashes: [
                  ...state.receivedPaidListingHashes,
                  ...payload.result.jobs
                    .map((job) =>
                      payload.keyToUseAsPrevious && job[payload.keyToUseAsPrevious]
                        ? job[payload.keyToUseAsPrevious]
                        : job.listingHash
                    )
                    .filter(Boolean),
                ],
              }
            : {}),
          [payload.type]: {
            ...prev,
            loading: false,
            pageNo: (prev.pageNo || 0) + 1,
            error: null,
            remainingJobs:
              payload.result.remainingJobs ||
              (payload.result.totalJobs || 0) - (payload.result.jobs?.length || 0),
            totalJobs: payload.result.totalJobs,
            // current jobs and new fetched jobs
            jobs: [...prev.jobs, ...payload.result.jobs],
            hideOnEmpty: payload.result.hideOnEmpty,
            ...(payload.result?.apiParams ? { apiParams: payload.result?.apiParams } : {}),
            filterDetail: {
              ...prev.filterDetail,
              // update previousListingHashes everytime
              previousListingHashes: filterBadValues([
                ...(prev.filterDetail?.previousListingHashes || []),
                ...payload.result.jobs.map((job) =>
                  payload.keyToUseAsPrevious ? job[payload.keyToUseAsPrevious] : job.listingHash
                ),
              ]),
            },
          },
        };
      }
      console.warn(`there is no ${payload.type} which you mentioned`);
      return state;
    case ACTION_TYPES.JOB_ADD_SAVE:
      return {
        ...state,
        favoriteJobs: payload.favoriteJobs,
        recentFavoriteJob: payload.job,
      };
    case ACTION_TYPES.JOB_REMOVE_SAVE:
      return {
        ...state,
        favoriteJobs: payload.favoriteJobs,
        recentFavoriteJob: {},
      };
    case ACTION_TYPES.JOB_ADD_DISMISS:
      return {
        ...state,
        dismissedJobs: _uniq([...dismissedJobs, payload.job.listingHash]),
        recentDismissedJob: payload.job,
      };
    case ACTION_TYPES.JOB_REMOVE_DISMISS:
      dismissedJobs.splice(dismissedJobs.indexOf(payload.job.listingHash), 1);
      return {
        ...state,
        dismissedJobs: dismissedJobs,
        recentDismissedJob: payload.job,
      };
    case ACTION_TYPES.JOB_ADD_SEEN:
      return {
        ...state,
        viewedJobs: _uniq([...state.viewedJobs, payload.job.listingHash]),
        recentViewedJob: payload.job,
      };
    case ACTION_TYPES.JOB_ADD_APPLIED:
      return {
        ...state,
        appliedJobs: [...state.appliedJobs, payload.job],
        recentAppliedJob: payload.job,
      };
    case ACTION_TYPES.LISTING_HASH:
      return {
        ...state,
        ...payload,
      };
    case ACTION_TYPES.JOB_CREATE_BUCKET: {
      const previousListingHashes =
        payload.detail?.filterDetail?.previousListingHashes ||
        (payload.detail?.jobs || []).map((job: Job) => job.listingHash) ||
        [];
      return {
        ...state,
        [payload.key]: {
          remainingJobs:
            payload.detail.remainingJobs ||
            (payload.detail?.totalJobs || 0) - (payload.detail?.jobs?.length || 0),
          ...JOBS,
          ...payload.detail,
          filterDetail: {
            ...(payload.detail?.filterDetail || {}),
            previousListingHashes,
          },
        },
      };
    }
    case ACTION_TYPES.JOB_DISPOSE_BUCKET: {
      const newState = Object.assign({}, state);
      delete newState[payload.key];
      return newState;
    }
    case ACTION_TYPES.CURRENT_PAGE_CATEGORY:
      return { ...state, ...{ currentPageCategory: payload } };
    case ACTION_TYPES.JOB_LIST_DISMISS:
      return {
        ...state,
        jobListDismiss: payload.jobListDismiss,
      };
    case ACTION_TYPES.ALERTS_RECENT_PAYLOAD_SET:
      return {
        ...state,
        alertsDetail: {
          ...state.alertsDetail,
          recentPayload: payload.recentPayload,
        },
      };
    case ACTION_TYPES.ALERTS_IS_LOADING_SET:
      return {
        ...state,
        alertsDetail: {
          ...state.alertsDetail,
          isLoading: payload.isLoading,
        },
      };
    case ACTION_TYPES.ALERTS_IS_SUBSCRIBED_SET:
      return {
        ...state,
        alertsDetail: {
          ...state.alertsDetail,
          isSubscribed: payload.isSubscribed,
        },
      };
    case ACTION_TYPES.RESET_FETCHED_PAID_HASHES:
      return {
        ...state,
        receivedPaidListingHashes: [],
      };
    default:
      return state;
  }
};

export default jobsReducer;
